'use client';

import { useTranslations } from '@/i18n/use-translations';
import logger from '@/utils/client-logger';
import { getSpmForAppLaunch } from '@/reporting/spm/spm-session';
import { useSpmDomAttr } from '@/reporting/spm/spm-dom';
import { getSpmPositionCode, useGlobalSpmTerminal } from '@/components/Layout/spmTerminal';
import { EPlatform } from '@/utils/enums/platform-enum';
import Image from 'next/image';
import type { MouseEvent } from 'react';
import appStoreImage from './icon/app-store.png';
import googlePlayImage from './icon/google-play.png';

const APP_DOWNLOAD_LINK = 'https://zeelool.onelink.me/QxSq/iw85lexv';

const DownloadApp = () => {
  const t = useTranslations('layout.footer');
  const spmPage = useGlobalSpmTerminal(EPlatform.WAP);
  const appStoreSpmCnt = getSpmPositionCode(
    spmPage,
    'footer_app',
    'open_download_on_the_app_store',
  );
  const androidSpmCnt = getSpmPositionCode(spmPage, 'footer_app', 'download_android_app');
  const appStoreDomSpm = useSpmDomAttr(appStoreSpmCnt);
  const androidDomSpm = useSpmDomAttr(androidSpmCnt);
  // 初始链接即携带完整 A.B.C.D，复制链接和原生新窗口打开也能识别下载入口。
  const appStoreDownloadLink = appStoreSpmCnt
    ? `${APP_DOWNLOAD_LINK}?spm=${encodeURIComponent(appStoreSpmCnt)}`
    : APP_DOWNLOAD_LINK;
  const androidDownloadLink = androidSpmCnt
    ? `${APP_DOWNLOAD_LINK}?spm=${encodeURIComponent(androidSpmCnt)}`
    : APP_DOWNLOAD_LINK;

  const updateDownloadLink = (event: MouseEvent<HTMLAnchorElement>, positionSpmCnt?: string) => {
    const url = new URL(APP_DOWNLOAD_LINK);
    if (positionSpmCnt) url.searchParams.set('spm', positionSpmCnt);
    event.currentTarget.href = url.toString();
    try {
      const pub_zl_utm_source =
        (window.parent as any).latestChannelAttribute?.expires > Date.now()
          ? (window.parent as any).latestChannelAttribute?.pub_zl_utm_source || undefined
          : undefined;
      const pub_zl_utm_medium =
        (window.parent as any).latestChannelAttribute?.expires > Date.now()
          ? (window.parent as any).latestChannelAttribute?.pub_zl_utm_medium || undefined
          : undefined;
      const pub_zl_utm_campaign =
        (window.parent as any).latestChannelAttribute?.expires > Date.now()
          ? (window.parent as any).latestChannelAttribute?.pub_zl_utm_campaign || undefined
          : undefined;
      const pub_zl_utm_campaignid =
        (window.parent as any).latestChannelAttribute?.expires > Date.now()
          ? (window.parent as any).latestChannelAttribute?.pub_zl_utm_campaignid || undefined
          : undefined;
      const pub_zl_referrer =
        (window.parent as any).latestChannelAttribute?.expires > Date.now()
          ? (window.parent as any).latestChannelAttribute?.pub_zl_referrer || undefined
          : undefined;
      const pub_zl_page =
        (window.parent as any).latestChannelAttribute?.expires > Date.now() ||
        (window.parent as any).latestChannelAttribute?.expires === -1
          ? (window.parent as any).latestChannelAttribute?.pub_zl_page || undefined
          : undefined;
      pub_zl_utm_source && url.searchParams.set('pub_zl_utm_source', pub_zl_utm_source);
      pub_zl_utm_medium && url.searchParams.set('pub_zl_utm_medium', pub_zl_utm_medium);
      pub_zl_utm_campaign && url.searchParams.set('pub_zl_utm_campaign', pub_zl_utm_campaign);
      pub_zl_utm_campaignid && url.searchParams.set('pub_zl_utm_campaignid', pub_zl_utm_campaignid);
      pub_zl_referrer && url.searchParams.set('pub_zl_referrer', pub_zl_referrer);
      pub_zl_page && url.searchParams.set('pub_zl_page', pub_zl_page);
      // 优先使用当前下载入口的 A.B.C.D；无对应坑位时才回落到最新来源候选。
      const spm = positionSpmCnt ?? getSpmForAppLaunch();
      spm && url.searchParams.set('spm', spm);
      event.currentTarget.href = url.toString();
    } catch (error) {
      logger.log('error', error);
    }
  };

  return (
    <section className="flex w-full flex-col items-center justify-center gap-[12px] rounded-[6px] bg-[#282828] py-[20px]">
      <p className="text-center font-br text-[13px] font-normal leading-none text-primary-white">
        {t('downloadApp.title')} {t('downloadApp.saveMore')}
      </p>
      <div className="flex items-center justify-center gap-[12px]">
        <a
          href={appStoreDownloadLink}
          onClick={(event) => updateDownloadLink(event, appStoreSpmCnt)}
          onAuxClick={(event) => updateDownloadLink(event, appStoreSpmCnt)}
          onContextMenu={(event) => updateDownloadLink(event, appStoreSpmCnt)}
          {...appStoreDomSpm}
          aria-label="Download on the App Store"
        >
          <Image
            className="h-[24px] w-[104px]"
            src={appStoreImage}
            alt={t('downloadApp.appStore')}
            width={104}
            height={24}
            unoptimized
          />
        </a>
        <a
          href={androidDownloadLink}
          onClick={(event) => updateDownloadLink(event, androidSpmCnt)}
          onAuxClick={(event) => updateDownloadLink(event, androidSpmCnt)}
          onContextMenu={(event) => updateDownloadLink(event, androidSpmCnt)}
          {...androidDomSpm}
          aria-label="Download on Google Play"
        >
          <Image
            className="h-[24px] w-[104px]"
            src={googlePlayImage}
            alt={t('downloadApp.googlePlay')}
            width={104}
            height={24}
            unoptimized
          />
        </a>
      </div>
    </section>
  );
};

export default DownloadApp;
